import { toBookingItem } from "@/src/shared/features/booking/bookingDetails";
import type {
  IBookingAdvance,
  IBookingItem,
  IBookingListApiResponse,
  IBookingListQuery,
  IBookingListResponse,
} from "@/src/shared/features/booking/types";

/** Booking status names the pages use → the numeric codes the Python API filters on. */
const STATUS_CODES: Record<string, number> = {
  requested: 0,
  accepted: 1,
  rejected: 2,
  partial: 3,
  confirmed: 4,
  refund_pending: 5,
  refunded: 6,
  canceled: 7,
  cancelled: 7,
};

export const bookingService = {
  /** "confirmed,partial" → "4,3"; unknown names are dropped; undefined when none remain. */
  statusCodes(names?: string): string | undefined {
    const codes = names
      ?.split(",")
      .map((name) => STATUS_CODES[name.trim().toLowerCase()])
      .filter((code): code is number => code !== undefined)
      .join(",");
    return codes || undefined;
  },

  /** Python list response → the Laravel-shaped page the booking pages read. */
  toListPage(
    response: IBookingListApiResponse,
    query: IBookingListQuery,
  ): IBookingListResponse {
    const pagination = response.pagination ?? {};
    return {
      data: (response.data ?? []).map(toBookingItem),
      current_page: pagination.page ?? query.page,
      per_page: pagination.per_page ?? query.limit,
      total: pagination.total ?? 0,
      last_page: Math.max(1, pagination.total_pages ?? 1),
    };
  },

  /**
   * The `begin_checkout` event for a payment the guest is starting: `value` is what this
   * payment charges (all of it, or the part chosen), not the nightly price.
   */
  checkoutEvent(booking: IBookingItem, amount: number) {
    return {
      item: booking.listing,
      value: amount,
      currency: "BDT",
      params: {
        booking_id: booking.id,
        booking_dates: `${booking.from} to ${booking.to}`,
        guests: booking.total_guest,
        total_payable: Number(booking.total_payable) || 0,
        payment_type:
          amount <
          (Number(booking.total_payable) || 0) - (Number(booking.paid) || 0)
            ? "partial"
            : "full",
      },
    };
  },

  hasBookings(page: IBookingListResponse | undefined): boolean {
    return Array.isArray(page?.data) && page.data.length > 0;
  },

  /** Arrival info; older bookings store it as a JSON string. Null when absent or unreadable. */
  advance(booking: Pick<IBookingItem, "advance">): IBookingAdvance | null {
    const advance = booking.advance;
    if (typeof advance !== "string") return advance ?? null;
    try {
      const parsed: unknown = JSON.parse(advance);
      return parsed && typeof parsed === "object"
        ? (parsed as IBookingAdvance)
        : null;
    } catch {
      return null;
    }
  },
};
